import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Header from "components/ui/Header";
import Breadcrumb from "components/ui/Breadcrumb";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import StatusIndicator from "components/ui/StatusIndicator";
import TrainingProgressIndicator from "components/ui/TrainingProgressIndicator";
import CourseCard from "./components/CourseCard";
import FilterPanel from "./components/FilterPanel";
import CoursePreviewModal from "./components/CoursePreviewModal";
import BookingConfirmModal from "./components/BookingConfirmModal";
import SearchBar from "./components/SearchBar";

const MOCK_USER = {
  name: "Alex Johnson",
  email: "alex.johnson@email.com",
  status: "new",
};

const MOCK_COURSES = [
  {
    id: 1,
    title: "Volunteer Orientation & Onboarding",
    description: "Essential introduction to our organization's mission, values, and volunteer expectations.",
    fullDescription: "This comprehensive orientation course is designed for all new volunteers. You will learn about our organization's history, mission, and core values. The course covers volunteer rights and responsibilities, code of conduct, communication protocols, and how to navigate our volunteer management system. By the end, you will be fully prepared to begin your volunteer journey with confidence.",
    category: "Orientation",
    format: "In-Person",
    duration: "3 hours",
    nextSession: "03/10/2026",
    instructor: "Sarah Mitchell",
    capacity: 30,
    enrolled: 18,
    status: "available",
    icon: "BookOpen",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Community Center, Room 101",
    prerequisites: [],
    syllabus: [
      { title: "Welcome & Organization Overview", duration: "45 min" },
      { title: "Volunteer Rights & Responsibilities", duration: "45 min" },
      { title: "Code of Conduct & Ethics", duration: "30 min" },
      { title: "Communication & Reporting", duration: "30 min" },
      { title: "Q&A and Next Steps", duration: "30 min" },
    ],
  },
  {
    id: 2,
    title: "Basic First Aid & Safety Protocols",
    description: "Learn essential first aid techniques and safety procedures required for all field volunteers.",
    fullDescription: "This hands-on safety training equips volunteers with critical first aid knowledge and emergency response skills. Participants will practice CPR, wound care, and learn how to handle common emergencies in volunteer settings. Certification is provided upon successful completion.",
    category: "Safety",
    format: "In-Person",
    duration: "Full Day",
    nextSession: "03/15/2026",
    instructor: "Dr. James Carter",
    capacity: 20,
    enrolled: 20,
    status: "available",
    icon: "HeartPulse",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Training Facility, Building B",
    prerequisites: ["Volunteer Orientation"],
    syllabus: [
      { title: "Introduction to First Aid", duration: "1 hour" },
      { title: "CPR & AED Training", duration: "2 hours" },
      { title: "Wound Care & Bandaging", duration: "1.5 hours" },
      { title: "Emergency Response Scenarios", duration: "2 hours" },
      { title: "Certification Assessment", duration: "1.5 hours" },
    ],
  },
  {
    id: 3,
    title: "Effective Communication for Volunteers",
    description: "Develop strong interpersonal and communication skills to work effectively with communities.",
    fullDescription: "This course focuses on building communication skills essential for volunteer work. Topics include active listening, conflict resolution, cultural sensitivity, and how to communicate effectively with diverse populations. Interactive role-playing exercises reinforce learning.",
    category: "Communication",
    format: "Online",
    duration: "2 hours",
    nextSession: "03/12/2026",
    instructor: "Linda Torres",
    capacity: 50,
    enrolled: 23,
    status: "available",
    icon: "MessageCircle",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Online via Zoom",
    prerequisites: [],
    syllabus: [
      { title: "Active Listening Techniques", duration: "30 min" },
      { title: "Non-Verbal Communication", duration: "20 min" },
      { title: "Cultural Sensitivity", duration: "30 min" },
      { title: "Conflict Resolution Basics", duration: "40 min" },
    ],
  },
  {
    id: 4,
    title: "Community Outreach Fundamentals",
    description: "Understand how to engage and support community members through outreach programs.",
    fullDescription: "This course provides volunteers with the tools and strategies needed for effective community outreach. Learn how to identify community needs, build trust with residents, coordinate outreach events, and measure impact. Case studies from real programs are included.",
    category: "Community",
    format: "Hybrid",
    duration: "Half Day",
    nextSession: "03/20/2026",
    instructor: "Marcus Williams",
    capacity: 25,
    enrolled: 10,
    status: "available",
    icon: "Users",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Community Hall & Online",
    prerequisites: ["Volunteer Orientation"],
    syllabus: [
      { title: "Understanding Community Needs", duration: "1 hour" },
      { title: "Building Trust & Relationships", duration: "1 hour" },
      { title: "Outreach Event Planning", duration: "1 hour" },
      { title: "Impact Measurement", duration: "1 hour" },
    ],
  },
  {
    id: 5,
    title: "Mental Health Awareness in Volunteering",
    description: "Recognize signs of mental health challenges and learn how to support vulnerable individuals.",
    fullDescription: "Volunteers often work with individuals facing mental health challenges. This course provides awareness training on common mental health conditions, how to approach sensitive conversations, and when to refer individuals to professional support. Self-care strategies for volunteers are also covered.",
    category: "Health",
    format: "Online",
    duration: "2-4 hours",
    nextSession: "03/25/2026",
    instructor: "Dr. Priya Nair",
    capacity: 40,
    enrolled: 15,
    status: "available",
    icon: "Brain",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Online via Teams",
    prerequisites: ["Effective Communication for Volunteers"],
    syllabus: [
      { title: "Introduction to Mental Health", duration: "45 min" },
      { title: "Recognizing Signs of Distress", duration: "45 min" },
      { title: "Supportive Conversations", duration: "45 min" },
      { title: "Referral Pathways", duration: "30 min" },
      { title: "Volunteer Self-Care", duration: "15 min" },
    ],
  },
  {
    id: 6,
    title: "Leadership & Team Coordination",
    description: "Advanced leadership skills for experienced volunteers ready to take on coordination roles.",
    fullDescription: "Designed for established and senior volunteers, this course covers leadership principles, team management, conflict resolution at a team level, and how to mentor newer volunteers. Participants will develop a personal leadership action plan.",
    category: "Leadership",
    format: "In-Person",
    duration: "Full Day",
    nextSession: "04/05/2026",
    instructor: "Robert Chen",
    capacity: 15,
    enrolled: 8,
    status: "upcoming",
    icon: "Star",
    requiredStatus: ["established", "senior"],
    location: "Leadership Center, Floor 3",
    prerequisites: ["Community Outreach Fundamentals", "Effective Communication"],
    syllabus: [
      { title: "Leadership Styles & Principles", duration: "1.5 hours" },
      { title: "Team Dynamics & Management", duration: "2 hours" },
      { title: "Mentoring New Volunteers", duration: "1.5 hours" },
      { title: "Conflict Resolution at Scale", duration: "1.5 hours" },
      { title: "Personal Leadership Plan", duration: "1.5 hours" },
    ],
  },
  {
    id: 7,
    title: "Digital Tools for Volunteer Management",
    description: "Master the digital platforms and tools used to coordinate and track volunteer activities.",
    fullDescription: "This practical course walks volunteers through the digital tools used in our organization including the volunteer management portal, scheduling apps, communication platforms, and reporting tools. Hands-on exercises ensure proficiency.",
    category: "Orientation",
    format: "Online",
    duration: "Under 2 hours",
    nextSession: "03/08/2026",
    instructor: "Emily Park",
    capacity: 60,
    enrolled: 34,
    status: "available",
    icon: "Monitor",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Online Self-Paced",
    prerequisites: [],
    syllabus: [
      { title: "Volunteer Portal Navigation", duration: "20 min" },
      { title: "Scheduling & Availability Tools", duration: "20 min" },
      { title: "Communication Platforms", duration: "20 min" },
      { title: "Reporting & Logging Hours", duration: "20 min" },
    ],
  },
  {
    id: 8,
    title: "Child & Youth Safeguarding",
    description: "Critical safeguarding training for volunteers working with children and young people.",
    fullDescription: "This mandatory course for volunteers working with minors covers safeguarding legislation, recognizing abuse and neglect, reporting procedures, and maintaining appropriate boundaries. Completion is required before working with any youth programs.",
    category: "Safety",
    format: "Hybrid",
    duration: "Half Day",
    nextSession: "03/18/2026",
    instructor: "Angela Brooks",
    capacity: 20,
    enrolled: 12,
    status: "available",
    icon: "Shield",
    requiredStatus: ["new", "trained", "established", "senior"],
    location: "Youth Center & Online",
    prerequisites: ["Volunteer Orientation"],
    syllabus: [
      { title: "Safeguarding Legislation Overview", duration: "1 hour" },
      { title: "Recognizing Abuse & Neglect", duration: "1 hour" },
      { title: "Reporting Procedures", duration: "1 hour" },
      { title: "Maintaining Appropriate Boundaries", duration: "1 hour" },
    ],
  },
];

const MOCK_ENROLLED = [1, 7];

const DEFAULT_FILTERS = {
  category: "All",
  format: "All",
  duration: "All",
  statusReq: "All",
  sortBy: "Newest",
};

export default function TrainingCoursesCatalog() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState("");
  const [previewCourse, setPreviewCourse] = useState(null);
  const [bookingCourse, setBookingCourse] = useState(null);
  const [enrolledIds, setEnrolledIds] = useState(MOCK_ENROLLED);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const volunteerStatus = MOCK_USER?.status;
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/login-screen", { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const filteredCourses = useMemo(() => {
    let result = [...MOCK_COURSES];

    if (search?.trim()) {
      const q = search?.toLowerCase();
      result = result?.filter(
        (c) =>
          c?.title?.toLowerCase()?.includes(q) ||
          c?.description?.toLowerCase()?.includes(q) ||
          c?.instructor?.toLowerCase()?.includes(q) ||
          c?.category?.toLowerCase()?.includes(q)
      );
    }

    if (filters?.category !== "All") result = result?.filter((c) => c?.category === filters?.category);
    if (filters?.format !== "All") result = result?.filter((c) => c?.format === filters?.format);
    if (filters?.duration !== "All") result = result?.filter((c) => c?.duration === filters?.duration);
    if (filters?.statusReq !== "All") result = result?.filter((c) => c?.requiredStatus?.includes(filters?.statusReq));

    if (filters?.sortBy === "Alphabetical") result?.sort((a, b) => a?.title?.localeCompare(b?.title));
    else if (filters?.sortBy === "Duration") result?.sort((a, b) => a?.duration?.localeCompare(b?.duration));
    else if (filters?.sortBy === "Popularity") result?.sort((a, b) => b?.enrolled - a?.enrolled);

    return result;
  }, [filters, search]);

  const eligibleCount = filteredCourses?.filter((c) => c?.requiredStatus?.includes(volunteerStatus))?.length;

  const handleBook = (course) => setBookingCourse(course);
  const handleConfirmBooking = (course) => {
    setEnrolledIds((prev) => [...prev, course?.id]);
  };

  const enrolledCourses = MOCK_COURSES?.filter((c) => enrolledIds?.includes(c?.id));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header isAuthenticated user={MOCK_USER} onLogout={() => {}} />
      <main className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb />
        </div>

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[var(--color-foreground)] mb-1">
              Training Courses
            </h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Discover and enroll in training programs tailored to your volunteer journey
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusIndicator status={volunteerStatus} showTooltip />
            <Link to="/volunteer-dashboard">
              <Button variant="outline" size="sm" iconName="LayoutDashboard" iconPosition="left" iconSize={14}>
                Dashboard
              </Button>
            </Link>
          </div>
        </div>

        {/* My Enrolled Courses */}
        {enrolledCourses?.length > 0 && (
          <div className="mb-6">
            <h2 className="font-heading font-semibold text-base text-[var(--color-foreground)] mb-3 flex items-center gap-2">
              <Icon name="BookMarked" size={16} color="var(--color-primary)" />
              My Enrolled Courses
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {enrolledCourses?.map((course) => (
                <TrainingProgressIndicator
                  key={course?.id}
                  courseName={course?.title}
                  progress={course?.id === 7 ? 100 : 35}
                  status={course?.id === 7 ? "completed" : "in_progress"}
                  totalModules={course?.syllabus?.length}
                  completedModules={course?.id === 7 ? course?.syllabus?.length : 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total Courses", value: MOCK_COURSES?.length, icon: "BookOpen", color: "text-blue-600" },
            { label: "Available to You", value: eligibleCount, icon: "Unlock", color: "text-emerald-600" },
            { label: "Enrolled", value: enrolledIds?.length, icon: "CheckCircle2", color: "text-violet-600" },
            { label: "Showing", value: filteredCourses?.length, icon: "Filter", color: "text-orange-600" },
          ]?.map((stat) => (
            <div key={stat?.label} className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-3 md:p-4 flex items-center gap-3">
              <div className={`${stat?.color}`}>
                <Icon name={stat?.icon} size={20} color="currentColor" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg md:text-xl text-[var(--color-foreground)]">{stat?.value}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{stat?.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search + Mobile Filter Toggle */}
        <div className="flex gap-3 mb-5">
          <div className="flex-1">
            <SearchBar value={search} onChange={setSearch} resultCount={filteredCourses?.length} />
          </div>
          <button
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors whitespace-nowrap min-h-[44px]"
          >
            <Icon name="SlidersHorizontal" size={16} color="currentColor" />
            Filters
          </button>
        </div>

        {/* Main Layout */}
        <div className="flex gap-6">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-20">
              <FilterPanel
                filters={filters}
                onChange={setFilters}
                onReset={() => setFilters(DEFAULT_FILTERS)}
                resultCount={filteredCourses?.length}
                isMobile={false}
              />
            </div>
          </aside>

          {/* Course Grid */}
          <div className="flex-1 min-w-0">
            {filteredCourses?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-4">
                  <Icon name="SearchX" size={28} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="font-heading font-semibold text-base text-[var(--color-foreground)] mb-2">No courses found</h3>
                <p className="text-sm text-[var(--color-muted-foreground)] mb-4">Try adjusting your search or filters</p>
                <Button variant="outline" onClick={() => { setSearch(""); setFilters(DEFAULT_FILTERS); }}>
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {filteredCourses?.map((course) => (
                  <CourseCard
                    key={course?.id}
                    course={course}
                    onBook={handleBook}
                    onPreview={setPreviewCourse}
                    volunteerStatus={volunteerStatus}
                    isEnrolled={enrolledIds?.includes(course?.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Mobile Filter Slide-out */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[150] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-[var(--color-background)] overflow-y-auto p-4 shadow-[var(--shadow-xl)]">
            <FilterPanel
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(DEFAULT_FILTERS)}
              resultCount={filteredCourses?.length}
              isMobile
              onClose={() => setShowMobileFilters(false)}
            />
            <div className="mt-4">
              <Button variant="default" fullWidth onClick={() => setShowMobileFilters(false)}>
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Preview Modal */}
      {previewCourse && (
        <CoursePreviewModal
          course={previewCourse}
          onClose={() => setPreviewCourse(null)}
          onBook={handleBook}
          volunteerStatus={volunteerStatus}
        />
      )}
      {/* Booking Modal */}
      {bookingCourse && (
        <BookingConfirmModal
          course={bookingCourse}
          onClose={() => setBookingCourse(null)}
          onConfirm={handleConfirmBooking}
        />
      )}
      {/* Footer */}
      <footer className="border-t border-[var(--color-border)] bg-[var(--color-card)] mt-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[var(--color-primary)] flex items-center justify-center">
              <Icon name="Heart" size={12} color="#fff" />
            </div>
            <span className="font-heading font-semibold text-sm text-[var(--color-foreground)]">VolunteerHub</span>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            &copy; {new Date()?.getFullYear()} VolunteerHub. All rights reserved.
          </p>
          <div className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
            <Icon name="Shield" size={12} color="var(--color-success)" />
            <span>SSL Secured</span>
          </div>
        </div>
      </footer>
    </div>
  );
}